<script setup lang="ts">
import groupBy from 'lodash/groupBy'
import minBy from 'lodash/minBy'
import maxBy from 'lodash/maxBy'

import FreeReturn from '@/assets/free-return.svg?component'
import VoucherMinute from '@/assets/voucher-minute.svg?component'
import Delivery from '@/assets/delivery.svg?component'
import Days14Return from '@/assets/14-days-return.svg?component'
import type { DisclosureRoot } from '#build/components'
import type { NuxtError } from '#app'

interface Media {
  id: string;
  media: Record<string, any>;
}

const { params, path } = useRoute()
const { baseUrl } = useRuntimeConfig().public

const store = useStore()
const productsStore = useProductsStore()
const cartStore = useCartStore()
const { dataLayer } = useScriptGoogleTagManager()

const { isUpdating } = storeToRefs(cartStore)
const { currencyIsoCode } = storeToRefs(store)

const { data: mainId, error }: { data: Ref<string>, error: Ref<NuxtError<unknown | typeof RedirectError> | null> } = await useAsyncData(`product:${params.slug}`, () => productsStore.init(params.slug as string, path))

if (error.value) {
  if (error.value.cause instanceof RedirectError) {
    await navigateTo(`/${error.value.cause.message}`, { redirectCode: 301 })
  } else {
    throw createError({ statusCode: 404, statusMessage: 'Product not found' })
  }
}

const product = computed(() => productsStore.product(mainId.value))

/** utils >> */
const findOption = (combination: MaybeRefOrGetter<any>) => product.value.availableOptions.find((options: any) => Object.values(toValue(combination)).every(option => options.optionIds?.includes(option)))
const findOptionInCombination = (combination: any, optionId: string) => combination.options.find((option: any) => option.id === optionId)
const findLowestPriceProduct = (products: Array<any>) => minBy(products, 'calculatedCheapestPrice.unitPrice')
/** << utils */

const defaultCombination = product.value.options.reduce((combinations: Record<string, string>, { groupId, id }: { groupId: string, id: string }) => ({ ...combinations, [groupId]: id }), {})
const defaultOption = findOption(defaultCombination)

provide(ProductInjection, {
  product,
  mainId
})

const availableCombinations = computed(() => product.value.combinations.filter((combination: any) => !!combination.options.length))
const selectedCombination = ref(defaultCombination)
const currentOption = computed(() => findOption(selectedCombination))
const currentOptionExists = computed(() => !!currentOption.value)
const option = computed(() => currentOption.value || defaultOption)

watch(option, (option) => {
  window.history.pushState({}, '', `/${option.extensions.urlPrefix.prefix}/${option.seoUrls?.[0].seoPathInfo}`)
})

/** depends from selected combination >> */
const id = computed(() => option.value.id)
const reference = computed(() => option.value.customFields.product_reference || option.value.productNumber.substring(8, 0))
const price = computed(() => option.value.calculatedPrices[0] || option.value.calculatedPrice)
const listPrice = computed(() => option.value.calculatedPrices[0]?.listPrice)
const regulationPrice = computed(() => option.value.calculatedPrices[0]?.regulationPrice || listPrice.value)
const description = computed(() => option.value.description)
const infoLocation =  computed(() => option.value.customFields.product_printPlace)
const infoDuration = computed(() => option.value.customFields.product_printDuration)
const infoNumberOfParticipants = computed(() => option.value.customFields.product_numberOfParticipants)
const infoAdditionalInfo = computed(() => option.value.customFields.product_printAdditionalInfo)
const infoOutfit = computed(() => option.value.customFields.product_printOutfit)
const infoSeason = computed(() => option.value.customFields.product_printSeason)
const infoRequirements = computed(() => option.value.customFields.product_printRequirements)
/** << depends from selected combination */

const breadcrumbs = computed<Array<Record<string, any>>>(() => product.value.extensions.breadcrumbWithUrl.breadcrumbs)
const reviewsTotalCount = computed<number>(() => product.value.reviews.total)
const ratingAverage = computed<number>(() => product.value.ratingAverage)
const cover = computed<Media>(() => product.value.cover)
const available = computed<boolean>(() => product.value.available)
const gallery = computed<Array<Media | undefined>>(() => cover.value ? product.value.media.filter((media: any) => media.id !== cover.value.id) : product.value.media);
const video = computed<string>(() => product.value.customFields.product_video?.match(/(^|=|\/)([0-9A-Za-z_-]{11})(\/|&|$|\?|#)/)[2])
const videoCover = computed<Media | undefined>(() => gallery.value?.[0])
const currentLocation = computed(() => product.value.customFields.product_seoLocation)
const locations = computed(() => groupBy(product.value.locations, 'customFields.product_seoLocation'))
const parentUrl = computed(() => breadcrumbs.value[breadcrumbs.value.length - 1])
const prefix = computed(() => product.value.extensions.urlPrefix.prefix)
const lowPrice = computed(() => product.value.calculatedCheapestPrice.unitPrice)
const hightPrice = computed(() => (maxBy(product.value.availableOptions, 'calculatedPrice.unitPrice') as any).calculatedPrice.unitPrice)
const schema = computed(() => product.value.customFields.product_schema)

const place = computed(() => ({
  id: mainId.value,
  name: product.value.customFields.product_printPlace,
  position: JSON.parse(product.value.customFields.product_pin),
  link: `/${prefix.value}/${product.value.seoUrls?.[0].seoPathInfo}`,
  icon: '/map/marker-selected.svg'
}))
const places = computed(() => [
  ...product.value.locations.map(({ id, customFields, seoUrls, extensions: { urlPrefix: { prefix } } }: any) => ({
    id,
    name: customFields.product_printPlace,
    position: JSON.parse(customFields.product_pin),
    link: `/${prefix}/${seoUrls?.[0].seoPathInfo}`,
    icon: '/map/marker.svg'
  })),
  place.value
])
const showLocations = computed(() => !!product.value.locations.length && product.value.customFields.product_productType !== 'Physical')
const isGiftCard = computed(() => product.value.customFields.product_productType === 'GiftCard')
const manufacturers = computed(() => product.value.manufacturers)
const crossSellingCategoryId = computed(() => breadcrumbs.value[breadcrumbs.value.length - 3]?.id)

const cartDialogOpen = ref<boolean>(false)
const showBuyTab = ref<boolean>(false)
const index = ref<number>(0)

const buyButtonRef = ref<HTMLElement | null>(null)
const locationsRef = ref<InstanceType<typeof DisclosureRoot> | null>(null)
const cartDialogFocusAfterClosed = ref<HTMLElement | null>(null)
const cartDialogInitialFocus = ref<HTMLElement | null>(null)
const tabs = reactive<Partial<Record<'info' | 'description' | 'map' | 'reviews' | 'faq', HTMLElement | null>>>({})

/** events >> */
const handleScrollToLocationClick = () => {
  locationsRef.value?.expand()
  locationsRef.value?.container?.scrollIntoView({ behavior: 'smooth' })
}

const addToCart = async (event: Event) => {
  event.stopPropagation()
  isUpdating.value = true
  cartDialogFocusAfterClosed.value = event.target as HTMLElement
  try {
    await cartStore.addProduct({ id: id.value })
    cartDialogOpen.value = true
    dataLayer.push({ ecommerce: null })
    dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        value: price.value.unitPrice,
        currency: currencyIsoCode.value,
        items: [{
          item_id: id.value,
          item_name: product.value.name,
          price: price.value.unitPrice,
          quantity: 1,
          item_brand: product.value.customFields.product_manufacturer,
          item_category: product.value.customFields.product_baseCategory,
          item_variant: `${option.value.options.map(({ group, name }: any) => `${group.name}: ${name}`).join(', ')}`,
          item_list_name: 'Produkt wariant'
        }]
      }
    })
  } catch {
    await refreshNuxtData(`product:${params.slug}`)
  } finally {
    isUpdating.value = false
  }
}

const addTab = (tabName: keyof typeof tabs) => (el: Element | ComponentPublicInstance | null) => tabs[tabName] = el as HTMLElement

const scrollTo = (tab: keyof typeof tabs) => tabs[tab]?.scrollIntoView({ behavior: 'smooth' })
/** << events */

useIntersectionObserver(buyButtonRef, ([{ isIntersecting, target }]) => {
  showBuyTab.value = !isIntersecting && target.getBoundingClientRect().top < 0
}, { rootMargin: '20px' })

const canonicalPath = computed(() => {
  if (parentUrl.value?.url && parentUrl.value?.urlPrefix) {
    return `/${parentUrl.value.urlPrefix}/${parentUrl.value.url}`
  }
  return path
})

const canonicalHref = computed(() => `${baseUrl}${canonicalPath.value}`)

useHead({
  titleTemplate: `${product.value.metaTitle} - %s`,
  meta: [{ hid: 'description', name: 'description', content: product.value.metaDescription }],
  link: [{
    hid: 'canonical',
    rel: 'canonical',
    href: canonicalHref.value,
  }],
  bodyAttrs: {
    class: 'mb-[150px] md:mb-[100px]'
  }
})

dataLayer.push({ ecommerce: null })
dataLayer.push({
  event: 'view_item',
  ecommerce: {
    value: product.value.calculatedPrice.unitPrice,
    currency: currencyIsoCode.value,
    items: [{
      item_id: product.value.id,
      item_name: product.value.name,
      price: product.value.calculatedPrice.unitPrice,
      quantity: 1,
      item_brand: product.value.customFields.product_manufacturer,
      item_category: product.value.customFields.product_baseCategory,
      item_variant: `${product.value.options.map(({ group, name }: any) => `${group.name}: ${name}`).join(', ')}`,
      item_list_name: 'Produkt wariant',
      discount: product.value.calculatedPrices[0]?.listPrice?.discount || null
    }]
  }
})

useSchemaOrg([
  defineBreadcrumb({
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@id': baseUrl,
          name: 'Go-Racing'
        }
      },
      ...breadcrumbs.value.map(({ name, url, urlPrefix }, index: number) => ({
        '@type': 'ListItem',
        position: index + 2,
        item: {
          '@id': `${baseUrl}/${urlPrefix}/${url}`,
          name
        }
      }))
    ]
  }),
  defineProduct({
    '@type': 'Product',
    name: product.value.name,
    description: product.value.metaDescription,
    brand: {
      '@type': 'Organization',
      name: 'Go-racing'
    },
    image: gallery.value.map(({ media: { alt, title, url, metaData: { height, width } } }) => ({
      '@type': 'ImageObject',
      url,
      contentUrl: url,
      description: alt,
      name: title,
      height,
      width
    })),
    category: parentUrl.value.name,
    sku: reference,
    url: `${baseUrl}/${parentUrl.value.urlPrefix}/${parentUrl.value.url}`,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: ratingAverage.value,
      reviewCount: reviewsTotalCount.value
    },
    offers: {
      '@type': 'AggregateOffer',
      availability: product.value.available ? 'InStock' : 'OutOfStock',
      price: price.value.unitPrice,
      priceCurrency: currencyIsoCode.value,
      lowPrice: lowPrice.value,
      highPrice: hightPrice.value,
      offerCount: product.value.availableOptions.length,
    } as any,
    review: product.value.reviews.elements.map(({ content, points, createdAt, extensions: { customerInfo: { name } } }: any) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name
      },
      datePublished: createdAt,
      reviewBody: content,
      reviewRating: {
        '@type': 'Rating',
        bestRating: '5',
        ratingValue: points,
        worstRating: '1'
      }
    }))
  })
])

const LeafletMap = loadWhenVisible({
  loader: () => import('@/components/LeafletMap.vue')
})

const ProductLeafletMap = loadWhenVisible({
  loader: () => import('@/components/ProductLeafletMap.vue'),
  options: {
    threshold: [0, 0.1, 0.25, 0.5, 0.75, 0.9, 1],
    rootMargin: '30px'
  }
})
</script>

<template>
  <div class="container mx-auto mb-[50px]">
    <div v-html="schema" />
    <Breadcrumbs :breadcrumbs="breadcrumbs" />
    <h1 class="text-30 font-medium mb-[10px]">
      {{ product.name }}
    </h1>
    <RatingDisplay
      as="button"
      :rating-average="ratingAverage"
      :reviews="reviewsTotalCount"
      class="mb-[10px]"
      @click="scrollTo('reviews')"
    />
    <div class="grid-product">
      <div class="[grid-area:gallery]">
        <Lightbox name="product-gallery" @update:modelValue="($index: number) => index = $index + (video ? 1 : 0)">
          <Carousel v-model="index" :options="{ loop: true }" class="relative">
            <Carousel.Slide v-if="video" class="shrink-0 basis-full">
              <Youtube :id="video" :img="videoCover?.media.url" />
            </Carousel.Slide>

            <Carousel.Slide v-for="(image, imageIndex) in gallery" :key="image.id" class="shrink-0 basis-full">
              <NuxtLink :to="image.media.url" :title="image.media.title" data-lightbox data-pswp-width="740" data-pswp-height="493">
                <Img :src="image.media.url" width="740" height="493" :alt="image.media.alt" :loading="!video && imageIndex === 0 ? 'eager' : 'lazy'" />
              </NuxtLink>
            </Carousel.Slide>

            <template #addons="{ prev, next }">
              <button aria-label="Poprzedni slajd" class="z-1 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center left-2" @click.stop="prev">
                <i class="icon-arrow-button -rotate-180" />
              </button>

              <button aria-label="Następny slajd" class="z-1 text-16 bg-white/60 absolute w-10 h-10 top-1/2 -translate-y-1/2 cursor-pointer rounded flex items-center justify-center right-2" @click.stop="next">
               <i class="icon-arrow-button" />
              </button>
            </template>
          </Carousel>
        </Lightbox>

        <Carousel role="tablist" :modelValue="index" :options="{ dragFree: true, containScroll: 'keepSnaps' }" class="relative mt-[5px] [--slide-spacing:5px]">
          <Carousel.Slide v-if="video" role="tab" :aria-selected="index === 0" class="shrink-0 basis-[28%] sm:basis-[26%] cursor-pointer transition-all ease-in-out duration-500 brightness-50 hover:brightness-100 aria-selected:brightness-100" @click="index = 0">
            <div class="relative h-full w-full flex items-center justify-center cursor-pointer">
              <div
                class="absolute flex items-center justify-center w-[30px] h-[20px] bg-youtube rounded-[15%/50%] cursor-pointer before:border-solid before:border-t-[6px] before:border-b-[6px] before:border-l-[12px] before:border-t-transparent before:border-r-transparent before:border-b-transparent before:border-l-white shadow-[rgb(0_0_0/60%)_0_0_30px]"
              />
              <Img v-if="videoCover" :src="videoCover?.media.url" width="740" height="493" :alt="videoCover?.media.alt" loading="eager" />
            </div>
          </Carousel.Slide>

          <Carousel.Slide v-for="(image, imageIndex) in gallery" :key="image.id" role="tab" :aria-selected="index === imageIndex + (video ? 1 : 0)" class="shrink-0 basis-[28%] sm:basis-[26%] cursor-pointer transition-all ease-in-out duration-500 brightness-50 hover:brightness-100 aria-selected:brightness-100" @click="index = imageIndex + (video ? 1 : 0)">
            <Img v-if="image" :src="image?.media.url" width="740" height="493" :alt="image.media.alt" loading="lazy" />
          </Carousel.Slide>
        </Carousel>

         <!-- ! promotion 20.04.2026 banner start -->
        <!-- <div v-if="!['f05a5279ad8f888dda342a7c6e458658', '575770113bb5f93b2906ef8917facca4', '75124f25b1db7620476f6d70697fec61', 'e92b755eb0d8800175a02a35c2bf44fe', '1aa4de654d4e502b4ef4136466b13fcc', '2ac5f9cb8a8e89382c2fc21937c21ae6', '5a12243d83e5d4340291fd74318bffe2'].includes(mainId)" class="my-[15px]">
          <NuxtLink to="/k/bestsellery" class="justify-center relative hidden xs:block">
            <Img
              src="/banners/produkt_mobile.jpg?t=1748327127"
              width="737"
              height="200"
              alt=""
            />
          </NuxtLink>
          <NuxtLink to="/k/bestsellery" class="justify-center relative hidden sm:block">
            <Img
              src="/banners/produkt_desktop.jpg?t=1748327127"
              width="740"
              height="200"
              alt=""
            />
          </NuxtLink>
        </div> -->
        <!-- ! promotion 03.05.2026 (including) banner end -->

      </div>
      <div class="[grid-area:right-column]">
        <div class="flex flex-col">
          <template v-if="showLocations">
            <div class="text-18 mt-[10px]">
              Wybierz lokalizację:
            </div>
            <Disclosure ref="locationsRef" outside-close>
              <div class="flex flex-col mt-[5px]">
                <Disclosure.Button class="group w-full text-left flex items-center justify-between p-[8px_8px_8px_4px] text-16 border-b border-gray-400">
                  <span>
                    <b>{{ currentLocation }}</b>
                    <span class="group-aria-expanded:hidden text-primary"> - kliknij aby zmienić</span>
                  </span>
                  <i class="icon-arrow-button text-[15px] transition-transform rotate-90 group-aria-expanded:-rotate-90" />
                </Disclosure.Button>
                <Disclosure.Panel #default="{ isOpen }">
                  <ul v-show="isOpen">
                    <li v-for="(products, location) in locations" :key="location" class="cursor-pointer">
                      <NuxtLink v-if="products.length == 1" :to="`/${products[0].extensions.urlPrefix.prefix}/${products[0].seoUrls?.[0].seoPathInfo}`" class="flex items-center justify-between p-[10px] transition-colors duration-300 ease-in-out hover:bg-primary hover:text-white focus:bg-primary focus:text-white">
                        <span>{{ location }}</span>
                        <Price :value="products[0].calculatedCheapestPrice.unitPrice" />
                      </NuxtLink>
                      <div v-else class="group/location">
                        <div tabindex="0" role="button" class="flex items-center justify-between p-[10px] transition-colors duration-300 ease-in-out group-hover/location:bg-secondary group-hover/location:text-white group-focus-within/location:bg-secondary group-focus-within/location:text-white">
                          {{ location }}
                          <span class="px-[10px] whitespace-nowrap">{{ products.length }} miejsca</span>
                          <Price :value="findLowestPriceProduct(products).calculatedCheapestPrice.unitPrice" />
                        </div>
                        <ul class="hidden group-hover/location:block group-focus-within/location:block">
                          <li v-for="p in products" :key="p.id">
                            <NuxtLink :to="`/${p.extensions.urlPrefix.prefix}/${p.seoUrls?.[0].seoPathInfo}`" class="flex items-center before:font-['icomoon'] before:content-['\e901'] before:text-10 before:mr-[10px] py-[10px] px-[20px] transition-colors duration-300 ease-in-out hover:bg-primary hover:text-white focus:bg-primary focus:text-white">
                              <span class="flex-auto">{{ p.name }}</span>
                              <Price :value="p.calculatedCheapestPrice.unitPrice" />
                            </NuxtLink>
                          </li>
                        </ul>
                      </div>
                    </li>
                  </ul>
                </Disclosure.Panel>
              </div>
            </Disclosure>
          </template>
          <template v-if="available">
            <div class="flex flex-col gap-[10px] mt-[10px]">
              <Disclosure.Group accordion :outside-close="!isGiftCard">
                <template v-for="combination in availableCombinations" :key="combination.id">
                  <Disclosure :open="isGiftCard">
                    <Disclosure.Button :disabled="isGiftCard" class="group w-full text-left flex items-center justify-between p-[8px_8px_8px_4px] text-16 border-b border-gray-400">
                      <span class="font-bold">
                        {{ combination.name }}:
                        <span class="text-primary">{{ findOptionInCombination(combination, selectedCombination[combination.id])?.name }}</span>
                      </span>
                      <i class="icon-arrow-button text-[15px] transition-transform rotate-90 group-aria-expanded:-rotate-90" />
                    </Disclosure.Button>
                    <Disclosure.Panel #default="{ isOpen }">
                      <div v-show="isOpen" class="flex flex-col">
                        <label v-for="option in combination.options" :key="option.id" tabindex="0" class="inline-flex items-center p-[10px] cursor-pointer transition-colors duration-300 ease-in-out hover:bg-secondary hover:text-white focus:bg-secondary focus:text-white">
                          <input v-model="selectedCombination[combination.id]" :name="combination.id" type="radio" :value="option.id" class="shrink-0 w-[14px] h-[14px] accent-action">
                          <span class="ml-[5px]">{{ option.name }}</span>
                        </label>
                      </div>
                    </Disclosure.Panel>
                  </Disclosure>
                </template>
              </Disclosure.Group>
            </div>
            <div class="flex justify-end gap-[15px] mt-[10px]">
              <div v-if="listPrice" class="flex items-center justify-end grow basis-0 gap-[15px]">
                <span class="text-16 font-medium px-[10px] bg-red text-white">-{{ Math.floor(listPrice.percentage) }}%</span>
                <Price :value="listPrice.price" class="text-18 line-through text-gray-500" />
              </div>
              <div class="flex items-center gap-x-[5px]">
                <span class="text-18">Cena:</span>
                <Price :value="price.unitPrice" class="text-24 font-medium" />
              </div>
            </div>
            <div v-if="listPrice && regulationPrice" class="text-12 text-end text-gray-400">
              <span>Najniższa cena z 30 dni przed obniżką: </span>
              <Price :value="regulationPrice.price" />
            </div>
            <div v-if="!currentOptionExists" class="text-red font-bold py-[5px]">
              Wybrana kombinacja jest niedostępna
            </div>
            <div class="flex flex-col gap-[10px] py-[5px]">
              <div class="flex items-center">
                <FreeReturn class="w-6 h-6 mr-2" />
                <span class="text-sm"><span class="text-green font-bold">Darmowa </span>wymiana</span>
              </div>
              <div class="flex items-center">
                <VoucherMinute class="w-6 h-6 mr-2" />
                <span class="text-sm"><span class="text-green font-bold">Voucher </span>w minutę na e-mail</span>
              </div>
              <div class="flex items-center">
                <Delivery class="w-6 h-6 mr-2" />
                <span class="text-sm">Darmowa dostawa od <span class="text-green font-bold">200 zł</span></span>
              </div>
              <div class="flex items-center">
                <Days14Return class="w-6 h-6 mr-2" />
                <span class="text-sm"><span class="text-green font-bold">14 dni </span>na zwrot</span>
              </div>
            </div>
            <button
              ref="buyButtonRef"
              class="btn btn-action !h-[50px] uppercase mt-[10px] disabled:pointer-events-none disabled:bg-action-disabled"
              :disabled="isUpdating || !currentOptionExists"
              @click="addToCart"
            >
              <Transition enter-active-class="animate-zoom-in" leave-active-class="animate-zoom-out">
                <Spinner v-if="isUpdating" class="w-[25px] h-[25px]" />
                <span v-else class="inline-flex pointer-events-none">Dodaj do koszyka</span>
              </Transition>
            </button>
            <ClientOnly>
              <Teleport to="body">
                <transition
                  :css="true"
                  enter-from-class="translate-y-full"
                  enter-active-class="duration-300 ease-in opacity-0"
                  enter-to-class="translate-y-0 opacity-100"
                  leave-active-class="duration-700 ease-out"
                  leave-to-class="translate-y-full opacity-0"
                >
                  <div v-if="showBuyTab" class="fixed z-10 bottom-0 right-0 left-0 bg-white py-[10px] shadow-[rgb(0_0_0/16%)_0_0_5px_1px]">
                    <div class="container mx-auto flex flex-col md:flex-row items-center gap-[10px]">
                      <div class="flex justify-between w-full flex-1">
                        <div class="flex flex-col justify-center items-start">
                          <div class="font-bold text-18 md:text-24 line-clamp-1">
                            {{ product.name }}
                          </div>
                          <button v-if="!!product.locations.length" class="font-bold" @click="handleScrollToLocationClick">
                            {{ currentLocation }} (zmień)
                          </button>
                        </div>
                        <div class="self-end flex flex-col items-end">
                          <div class="flex flex-nowrap gap-x-[5px]">
                            <span class="text-20">Cena:</span>
                            <Price :value="price.unitPrice" class="text-24 font-bold" />
                          </div>
                        </div>
                      </div>
                      <button :disabled="isUpdating || !currentOptionExists" class="btn btn-action !h-[50px] uppercase md:!w-auto md:min-w-[300px] disabled:pointer-events-none disabled:bg-action-disabled" @click="addToCart">
                        <Transition enter-active-class="animate-zoom-in" leave-active-class="animate-zoom-out">
                          <Spinner v-if="isUpdating" class="w-[25px] h-[25px]" />
                          <span v-else class="inline-flex pointer-events-none">Dodaj do koszyka</span>
                        </Transition>
                      </button>
                    </div>
                  </div>
                </transition>
              </Teleport>
            </ClientOnly>
          </template>
        </div>
        <PaymentMethods class="grayscale justify-evenly w-full py-[15px]" />
        <p class="text-right text-12">
          <span class="font-medium">Numer ref: </span>
          {{ reference }}
        </p>
        <div class="hidden md:block p-[20px] mt-[10px] bg-gray-200">
          <p class="text-primary text-16 font-medium mb-[20px]">
            Mapa dostępnych lokalizacji
          </p>
          <div class="relative z-0 w-auto h-full">
            <ClientOnly>
              <LeafletMap :places="places" class="min-h-[492px]" />
            </ClientOnly>
          </div>
        </div>
      </div>
      <div class="[grid-area:description]">
        <div class="relative mt-[5px]">
          <div class="flex relative overflow-x-scroll scrollbar-none bg-white border-b border-secondary-disabled">
            <button class="relative group grow text-16 text-primary py-[8px] px-[16px] font-medium whitespace-nowrap" @click="scrollTo('description')">
              <span class="block scale-115">Opis</span>
              <span class="flex absolute top-0 left-0 justify-center w-full h-full pointer-events-none z-[1]">
                <span class="border-t-2 border-primary self-end w-full" />
              </span>
            </button>

            <button class="group grow text-16 text-secondary py-[8px] px-[16px] font-medium whitespace-nowrap" @click="scrollTo('info')">
              <span class="block transition-transform will-change-transform duration-200 ease-in-out group-hover:scale-115">Informacje</span>
            </button>

            <button class="group grow text-16 text-secondary py-[8px] px-[16px] font-medium whitespace-nowrap" @click="scrollTo('map')">
              <span class="block transition-transform will-change-transform duration-200 ease-in-out group-hover:scale-115">Mapa</span>
            </button>

            <button class="group grow text-16 text-secondary py-[8px] px-[16px] font-medium whitespace-nowrap" @click="scrollTo('reviews')">
              <span class="block transition-transform will-change-transform duration-200 ease-in-out group-hover:scale-115">Opinie</span>
            </button>

            <button class="group grow text-16 text-secondary py-[8px] px-[16px] font-medium whitespace-nowrap" @click="scrollTo('faq')">
              <span class="block transition-transform will-change-transform duration-200 ease-in-out group-hover:scale-115">Zadaj pytanie</span>
            </button>
          </div>

          <div>
            <div :ref="addTab('description')">
              <Seo :content="description"  class="mt-[15px]" />
              <LazyYoutube v-if="product.customFields.product_youtubeId" :id="product.customFields.product_youtubeId" class="mb-[20px]" />
            </div>

            <div :ref="addTab('info')">
              <div class="text-20 font-bold text-primary mt-[30px]">
                Informacje
              </div>
              <div v-if="infoLocation" class="mt-[10px]">
                <span class="text-16 font-medium">Miejsce realizacji</span>
                <p class="mt-[5px]">
                  {{ infoLocation }}
                </p>
              </div>

              <div v-if="infoDuration" class="mt-[10px]">
                <span class="text-16 font-medium">Czas trwania</span>
                <p class="mt-[5px]">
                  {{ infoDuration }}
                </p>
              </div>

              <div v-if="infoNumberOfParticipants" class="mt-[10px]">
                <span class="text-16 font-medium">Ilość osób</span>
                <p class="mt-[5px]">
                  {{ infoNumberOfParticipants }}
                </p>
              </div>

              <div v-if="infoAdditionalInfo" class="mt-[10px]">
                <span class="text-16 font-medium">Informacje dodatkowe</span>
                <p class="mt-[5px]">
                  {{ infoAdditionalInfo }}
                </p>
              </div>

              <div v-if="infoOutfit" class="mt-[10px]">
                <span class="text-16 font-medium">Obowiązujący strój</span>
                <p class="mt-[5px]">
                  {{ infoOutfit }}
                </p>
              </div>

              <div v-if="infoSeason" class="mt-[10px]">
                <span class="text-16 font-medium">Sezon</span>
                <p class="mt-[5px]">
                  {{ infoSeason }}
                </p>
              </div>

              <div v-if="infoRequirements" class="mt-[10px]">
                <span class="text-16 font-medium">Wymagania</span>
                <p class="mt-[5px]">
                  {{ infoRequirements }}
                </p>
              </div>
            </div>

            <div :ref="addTab('map')">
              <div class="text-20 font-bold text-primary mt-[30px]">
                Mapa
              </div>
              <ClientOnly>
                <ProductLeafletMap :place="place" class="mt-[10px] min-h-[500px] h-[500px]" />
              </ClientOnly>
            </div>

            <div :ref="addTab('reviews')">
              <div class="text-20 font-bold text-primary mt-[30px]">
                Opinie
              </div>
              <Review class="p-[15px]"/>
            </div>

            <div :ref="addTab('faq')">
              <div class="text-20 font-bold text-primary mt-[30px]">
                Zadaj pytanie
              </div>
              <Faq class="p-[15px]"/>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!!manufacturers.length" class="mt-[20px]">
      <div class="text-16 font-bold text-primary">
        Realizacja
      </div>
      <ul class="flex flex-col gap-[10px] mt-[10px]">
        <li v-for="manufacturer in manufacturers" :key="manufacturer.id">
          <NuxtLink :to="`/${manufacturer.prefix}/${manufacturer.seoUrl}`" class="underline">
            {{ manufacturer.name }}
          </NuxtLink>
        </li>
      </ul>
    </div>

    <CrossSelling :category-id="crossSellingCategoryId" :id="mainId" class="mt-[30px]" />

    <ClientOnly>
      <Teleport to="body">
        <Transition.Dialog>
          <Dialog
            v-if="cartDialogOpen"
            esc-close
            :focus-after-closed="cartDialogFocusAfterClosed"
            :initial-focus="cartDialogInitialFocus"
            aria-labelledby="dialog_cart_label"
            class="flex"
            @close="cartDialogOpen = false"
          >
            <Dialog.Overlay closeable class="backdrop-blur bg-secondary/80" />
            <Dialog.Panel class="relative bg-white shadow-[rgb(0_0_0/20%)_0_3px_6px] overflow-hidden flex flex-col m-auto max-h-full w-full md:w-auto md:h-auto">
              <div class="flex items-center py-[10px] mx-[20px]">
                <h3 id="dialog_cart_label" class="font-medium text-20 text-action flex-1">
                  Dodaliśmy produkt do Twojego koszyka
                </h3>
                <Dialog.Close
                  type="button"
                  aria-label="Zamknij"
                  class="text-20 transition-colors duration-500 ease-in-out hover:text-red focus:text-red"
                >
                  <i class="icon-close" />
                </Dialog.Close>
              </div>
              <div class="w-full md:w-[500px] mb-[20px] p-[20px_20px_0_20px] overflow-auto">
                <div class="text-16 font-bold border-b border-solid border-gray-400 mb-[15px] pb-[15px]">
                  {{ product.name }}
                </div>
                <div class="flex justify-between mb-[15px]">
                  <span>Dostawa</span>
                  <span>Darmowa dostawa</span>
                </div>
                <div class="flex justify-between text-16 mb-[15px]">
                  <span class="font-bold">
                    Razem:
                  </span>
                  <Price :value="price.unitPrice" />
                </div>
                <button ref="cartDialogInitialFocus" class="btn btn-action !h-[50px] uppercase mt-[15px]" @click="navigateTo('/koszyk')">
                  Przejdź do koszyka
                </button>
                <Dialog.Close aria-label="Zamknij" class="btn btn-white !h-[50px] uppercase mt-[15px]">
                  Kontynuuj zakupy
                </Dialog.Close>
              </div>
            </Dialog.Panel>
          </Dialog>
        </Transition.Dialog>
      </Teleport>
    </ClientOnly>
  </div>
</template>

<style lang="scss" scoped>
.grid-product {
  @apply grid gap-x-[30px] auto-cols-fr;
  grid-template-areas:
    "gallery"
    "right-column"
    "description";

  @screen md {
    grid-template-columns: minmax(0, 2fr) 1fr;
    grid-template-areas:
      "gallery right-column"
      "description right-column";
    grid-template-rows: auto 1fr;
  }
}
</style>
